/**
 * Prévia de como a resposta aparece no WhatsApp.
 *
 * A secretaria escreve com a sintaxe do WhatsApp (`*negrito*`, `_itálico_`,
 * `~riscado~`, ```` ```mono``` ````) e precisa VER o resultado antes de salvar:
 * um asterisco desbalanceado chega ao aluno como asterisco literal, e isso só
 * é óbvio na renderização.
 *
 * Função PURA, sem React, para ter teste. Devolve uma árvore de nós, não HTML:
 * montar string de HTML e injetar com `dangerouslySetInnerHTML` seria um
 * vetor de XSS a partir de texto que vem do banco.
 */

export type No =
  | { tipo: 'texto'; valor: string }
  | { tipo: 'negrito'; filhos: No[] }
  | { tipo: 'italico'; filhos: No[] }
  | { tipo: 'riscado'; filhos: No[] }
  | { tipo: 'mono'; valor: string };

/** Uma linha da prévia. Linha vazia vira parágrafo em branco. */
export interface Linha {
  nos: No[];
}

interface Marcador {
  simbolo: string;
  tipo: 'negrito' | 'italico' | 'riscado';
}

const MARCADORES: Marcador[] = [
  { simbolo: '*', tipo: 'negrito' },
  { simbolo: '_', tipo: 'italico' },
  { simbolo: '~', tipo: 'riscado' },
];

/**
 * O WhatsApp só aplica o marcador quando ele abre e fecha COLADO ao conteúdo:
 *
 *   `*negrito*`   formata
 *   `*solto`      literal (não fecha)
 *   `**`          literal (nada dentro)
 *   `2 * 3 * 4`   literal (espaço depois do que abriria)
 *   `*texto *`    literal (espaço antes do que fecharia)
 *
 * A regra do espaço é o que impede multiplicação e bullet de virarem negrito —
 * e é exatamente o tipo de detalhe que a prévia existe para expor.
 */
function ehEspaco(c: string | undefined): boolean {
  return c === undefined || /\s/.test(c);
}
function parsearInline(texto: string): No[] {
  const nos: No[] = [];
  let acumulado = '';

  const despejar = (): void => {
    if (acumulado) {
      nos.push({ tipo: 'texto', valor: acumulado });
      acumulado = '';
    }
  };

  let i = 0;
  while (i < texto.length) {
    const c = texto[i] as string;

    // Monoespaçado com três backticks: não formata nada dentro.
    if (texto.startsWith('```', i)) {
      const fim = texto.indexOf('```', i + 3);
      if (fim > i + 3) {
        despejar();
        nos.push({ tipo: 'mono', valor: texto.slice(i + 3, fim) });
        i = fim + 3;
        continue;
      }
    }

    const marcador = MARCADORES.find((m) => m.simbolo === c);
    // O marcador que abre não pode ter espaço depois dele.
    if (marcador && !ehEspaco(texto[i + 1])) {
      // Procura um fechamento que não tenha espaço antes dele.
      let fim = texto.indexOf(marcador.simbolo, i + 1);
      while (fim > i + 1 && ehEspaco(texto[fim - 1])) {
        fim = texto.indexOf(marcador.simbolo, fim + 1);
      }
      if (fim > i + 1) {
        const dentro = texto.slice(i + 1, fim);
        if (dentro.length > 0) {
          despejar();
          nos.push({ tipo: marcador.tipo, filhos: parsearInline(dentro) });
          i = fim + 1;
          continue;
        }
      }
    }

    acumulado += c;
    i += 1;
  }

  despejar();
  return nos;
}

export function previaWhatsapp(texto: string): Linha[] {
  return texto.split('\n').map((linha) => ({ nos: parsearInline(linha) }));
}

/** Texto puro da prévia — usado em teste e para contar caracteres. */
export function textoSemMarcacao(texto: string): string {
  const planificar = (nos: No[]): string =>
    nos
      .map((no) =>
        no.tipo === 'texto' ? no.valor : no.tipo === 'mono' ? no.valor : planificar(no.filhos),
      )
      .join('');
  return previaWhatsapp(texto)
    .map((l) => planificar(l.nos))
    .join('\n');
}
